import type { StoredCalendarEvent, StoredCalendarEventRow, UserAuth } from '@repo/common';
import type { BaseModel } from '@repo/common/types/Sqlite';
import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';
import type { StoredCalendarEventCommonItem as CommonStoredCalendarEventCommonItem } from '@repo/common/types/Adapter';

export type StoredCalendarEventCommonItem = CommonStoredCalendarEventCommonItem;

export class StoredCalendarEventClientAdapter extends PrivateClientDatabaseAdapter<
  StoredCalendarEventRow,
  StoredCalendarEventCommonItem
> {
  constructor(
    sqliteModel: BaseModel<StoredCalendarEvent, StoredCalendarEventRow>,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
    }>
  ) {
    super(sqliteModel, 'calendarEvent', userId, authToken, adapterConfig);
  }

  protected sqliteRowToItem(row: StoredCalendarEventRow): StoredCalendarEventCommonItem {
    return {
      id: row.id,
      url: row.url || undefined,
      calendarId: row.calendarId || undefined,
      workspaceId: row.workspaceId || undefined,
      transcriptId: row.transcriptId || undefined,
      data: row.data ? parse(row.data) : undefined,
      tags: row.tags ? parse(row.tags) : [],
      participants: row.participants ? parse(row.participants) : [],
      userId: this.userId, // Use injected userId since SQLite doesn't have it
      isFavorite: row.isFavorite === true || row.isFavorite === 1 || row.isFavorite === 'true',
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined,
      pushedAt: row.pushedAt ? new Date(row.pushedAt) : undefined
    };
  }

  protected itemToSqliteRow(item: StoredCalendarEventCommonItem): StoredCalendarEventRow {
    return {
      id: item.id,
      url: item.url || null,
      calendarId: item.calendarId || null,
      workspaceId: item.workspaceId || null,
      transcriptId: item.transcriptId || null,
      data: item.data ? stringify(item.data) : null,
      tags: item.tags ? stringify(item.tags) : null,
      participants: item.participants ? stringify(item.participants) : null,
      isFavorite: item.isFavorite ? 1 : 0,
      pushedAt: item.pushedAt ? item.pushedAt.toISOString() : undefined,
      createdAt: item.createdAt.toISOString(),
      updatedAt: item.updatedAt.toISOString(),
      deletedAt: item.deletedAt ? item.deletedAt.toISOString() : null
    };
  }

  protected async getAllFromSqlite(): Promise<StoredCalendarEventRow[]> {
    const events = await this.sqliteModel.findAllObj({ paranoid: true });
    return events.map((event) => ({
      id: event.id,
      url: event.url || null,
      calendarId: event.calendarId || null,
      workspaceId: event.workspaceId || null,
      transcriptId: event.transcriptId || null,
      data: event.data ? stringify(event.data) : null,
      tags: event.tags ? stringify(event.tags) : null,
      participants: event.participants ? stringify(event.participants) : null,
      isFavorite: event.isFavorite ? 1 : 0,
      pushedAt: event.pushedAt?.toISOString(),
      createdAt: event.createdAt?.toISOString() || new Date().toISOString(),
      updatedAt: event.updatedAt?.toISOString() || new Date().toISOString(),
      deletedAt: event.deletedAt?.toISOString() || null
    }));
  }

  protected async getFromSqliteById(id: string): Promise<StoredCalendarEventRow | null> {
    console.log(`[StoredCalendarEventAdapter] Looking for calendar event ${id} in SQLite`);

    const events = await this.sqliteModel.findAllObj({
      where: { id },
      limit: 1,
      paranoid: true
    });

    if (events.length === 0) {
      console.log(`[StoredCalendarEventAdapter] Calendar event ${id} not found in SQLite`);
      return null;
    }

    const event = events[0];
    console.log(`[StoredCalendarEventAdapter] Found calendar event ${id} in SQLite:`, {
      id: event.id,
      workspaceId: event.workspaceId,
      updatedAt: event.updatedAt
    });

    return {
      id: event.id,
      url: event.url || null,
      calendarId: event.calendarId || null,
      workspaceId: event.workspaceId || null,
      transcriptId: event.transcriptId || null,
      data: event.data ? stringify(event.data) : null,
      tags: event.tags ? stringify(event.tags) : null,
      participants: event.participants ? stringify(event.participants) : null,
      isFavorite: event.isFavorite ? 1 : 0,
      pushedAt: event.pushedAt?.toISOString(),
      createdAt: event.createdAt?.toISOString() || new Date().toISOString(),
      updatedAt: event.updatedAt?.toISOString() || new Date().toISOString(),
      deletedAt: event.deletedAt?.toISOString() || null
    };
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<StoredCalendarEventCommonItem> {
    return {
      type: 'none' as const,
      getKey: (item: StoredCalendarEventCommonItem) => item.id
    };
  }

  /**
   * Override validateItemEquality to use calendar event-specific validation
   * Since there's no specific validator in ValidationUtils, we use the base adapter's logic
   * but with calendar event-specific logging and field handling
   */
  protected validateItemEquality(
    item1: StoredCalendarEventCommonItem,
    item2: StoredCalendarEventCommonItem
  ): { isEqual: boolean; differences: string[] } {
    console.log(`[DEBUG] Calendar event client adapter validateItemEquality called`);

    // Use the base adapter's validation logic but with calendar event-specific considerations
    const differences: string[] = [];

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(item1), ...Object.keys(item2)]);

    for (const key of allKeys) {
      const val1 = (item1 as any)[key];
      const val2 = (item2 as any)[key];

      if (key === 'createdAt' || key === 'updatedAt' || key === 'deletedAt' || key === 'pushedAt') {
        // Handle date comparison with 1 second tolerance
        if (val1 && val2) {
          const time1 = val1 instanceof Date ? val1.getTime() : new Date(val1).getTime();
          const time2 = val2 instanceof Date ? val2.getTime() : new Date(val2).getTime();

          if (Math.abs(time1 - time2) > 1000) {
            differences.push(
              `Date field '${key}' differs by more than 1 second: ${val1} vs ${val2}`
            );
          }
        } else if (val1 !== val2) {
          differences.push(`Date field '${key}' differs: ${val1} vs ${val2}`);
        }
      } else if (key === 'data' || key === 'tags' || key === 'participants') {
        // Handle JSON data comparison with deep equality
        if (!this.deepEqual(val1, val2)) {
          differences.push(
            `JSON field '${key}' differs: ${JSON.stringify(val1)} vs ${JSON.stringify(val2)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (val1 !== val2) {
          differences.push(`Field '${key}' differs: ${val1} vs ${val2}`);
        }
      }
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }
}
